import { useState, useEffect } from 'react';
import { DataStore } from 'aws-amplify';
import { SSCC } from '../models';
import { SSCCProps, convertSsccToProps } from '../datastore/ssccs';

export default function useObserveSscc(ssccId: string) {
  const [sscc, setSscc] = useState<SSCCProps>();
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    async function fetchInitialSSCC() {
      try {
        const initialSSCC = await DataStore.query(SSCC, ssccId);
        if (!initialSSCC) return;
        const convertedSscc = convertSsccToProps(initialSSCC);
        setSscc(convertedSscc);
      } catch (error) {
        console.error('Error fetching initial SSCC:', error);
      }
    }

    fetchInitialSSCC();

    const subscription = DataStore.observe(SSCC, ssccId).subscribe({
      next: (update) => {
        console.log('SSCC update received:', update);
        const convertedSscc = convertSsccToProps(update.element);
        setSscc(convertedSscc);
      },
      error: (error) => console.error('Error observing SSCC:', error)
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [ssccId]);

  // Handle network status changes
  useEffect(() => {
    const handleOnline = async () => {
      console.log('Network reconnected - refreshing SSCC data');
      setIsOnline(true);

      try {
        // Force refresh when coming back online to prevent stale data
        const freshSscc = await DataStore.query(SSCC, ssccId);
        if (freshSscc) {
          const convertedSscc = convertSsccToProps(freshSscc);
          setSscc(convertedSscc);
          console.log('SSCC data refreshed after reconnection');
        }
      } catch (error) {
        console.error('Error refreshing SSCC after reconnection:', error);
      }
    };

    const handleOffline = () => {
      console.log('Network disconnected');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [ssccId]);

  return sscc;
}
